import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Minus, Plus, ShoppingCart } from 'lucide-react';
import { Page } from '@shared/ui/containers/Page';
import { Card } from '@shared/ui/containers/Card';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Button } from '@shared/ui/primitives/Button';
import { formatMoney } from '@shared/lib/format';
import { toast } from '@shared/ui/feedback/Toast';
import { addCartItem } from '@features/cart/api';
import { fetchProduct } from './api';

export function ProductDetail(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [quantity, setQuantity] = useState(1);
  const [variantId, setVariantId] = useState<string | undefined>();

  const inApp = location.pathname.startsWith('/app/');
  const productsRoute = inApp ? '/app/products' : '/products/search';
  const cartRoute = inApp ? '/app/cart' : '/cart';

  const productQuery = useQuery({
    queryKey: ['product', id],
    queryFn: () => fetchProduct(id),
    enabled: !!id,
  });

  const product = productQuery.data;
  const images = product?.images ?? [];
  const selectedVariant = product?.variants?.find((variant) => variant.variant_id === variantId);
  const displayPrice = selectedVariant?.price ?? product?.price ?? 0;
  const hasVariants = Boolean(product?.variants?.length);
  const inStock = hasVariants ? selectedVariant?.in_stock === true : product?.in_stock === true;
  const heroImage = useMemo(
    () => images[0]?.url ?? product?.thumbnail_url,
    [images, product?.thumbnail_url],
  );

  useEffect(() => {
    if (!product?.variants?.length) {
      setVariantId(undefined);
      return;
    }
    if (variantId && product.variants.some((variant) => variant.variant_id === variantId)) return;
    const firstAvailable = product.variants.find((variant) => variant.in_stock) ?? product.variants[0];
    if (firstAvailable) setVariantId(firstAvailable.variant_id);
  }, [product, variantId]);

  const addMutation = useMutation({
    mutationFn: () => {
      if (!product) throw new Error('Product is not loaded yet.');
      return addCartItem({
        product_id: product.product_id,
        variant_id: variantId,
        quantity,
        name: selectedVariant ? `${product.name} - ${selectedVariant.label}` : product.name,
        unit_price: displayPrice,
        currency: product.currency,
        thumbnail_url: heroImage,
      });
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(['cart'], cart);
      toast.success('Added to cart');
    },
    onError: (error) => {
      toast.danger("Couldn't add item", error instanceof Error ? error.message : undefined);
    },
  });

  if (productQuery.isLoading) {
    return (
      <Page>
        <Skeleton className="aspect-[4/5]" />
        <div className="mt-5 space-y-3">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-20" />
        </div>
      </Page>
    );
  }

  if (productQuery.isError) {
    return (
      <Page>
        <ErrorState onRetry={() => productQuery.refetch()} />
      </Page>
    );
  }

  if (!product) {
    return (
      <Page>
        <EmptyState
          icon={<ShoppingCart className="h-7 w-7" strokeWidth={1.5} />}
          title="Product not found."
          description="This item may no longer be available."
          action={<Button onClick={() => navigate(productsRoute)}>Back to products</Button>}
        />
      </Page>
    );
  }

  return (
    <Page>
      <button
        type="button"
        onClick={() => navigate(productsRoute)}
        className="mb-3 inline-flex items-center gap-1 text-body-sm text-ink-secondary"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
        Products
      </button>

      <Card noGrain className="overflow-hidden p-0">
        <div className="aspect-[4/5] bg-sunken">
          {heroImage ? (
            <img src={heroImage} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-ink-muted">
              <ShoppingCart className="h-10 w-10" strokeWidth={1.25} />
            </div>
          )}
        </div>
      </Card>

      <section className="mt-5">
        {product.category ? <span className="eyebrow">{product.category}</span> : null}
        <div className="mt-1 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="display-serif text-display-lg text-ink">{product.name}</h1>
            {product.sku ? <p className="mt-1 text-caption text-ink-muted">SKU {product.sku}</p> : null}
          </div>
          <p className="shrink-0 font-display text-headline text-ink tabular">
            {formatMoney(displayPrice, { currency: product.currency })}
          </p>
        </div>
      </section>

      {product.description ? (
        <Card className="mt-5">
          <p className="whitespace-pre-line text-body text-ink-secondary">{product.description}</p>
        </Card>
      ) : null}

      {product.variants?.length ? (
        <section className="mt-5">
          <p className="mb-2 text-label text-ink">Choose an option</p>
          <div className="flex flex-col gap-2">
            {product.variants.map((variant) => {
              const selected = variant.variant_id === variantId;
              return (
                <button
                  key={variant.variant_id}
                  type="button"
                  disabled={!variant.in_stock}
                  onClick={() => setVariantId(variant.variant_id)}
                  className={
                    'flex items-center justify-between rounded-md bg-card px-4 py-3 text-left hairline transition-colors duration-fast disabled:opacity-50 ' +
                    (selected ? 'border-primary-500 text-ink' : 'text-ink-secondary hover:bg-sunken')
                  }
                >
                  <span>
                    <span className="block text-body font-semibold">{variant.label}</span>
                    {!variant.in_stock ? <span className="text-caption text-ink-muted">Out of stock</span> : null}
                  </span>
                  <span className="flex items-center gap-2 text-body-sm tabular">
                    {formatMoney(variant.price ?? product.price, { currency: product.currency })}
                    {selected ? <Check className="h-4 w-4 text-primary-600" strokeWidth={2} /> : null}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      <div className="mt-6 flex items-center justify-between rounded-lg bg-card p-3 hairline">
        <span className="text-label text-ink">Quantity</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Decrease quantity"
            onClick={() => setQuantity((value) => Math.max(1, value - 1))}
            className="flex h-9 w-9 items-center justify-center rounded-pill bg-sunken text-ink-muted"
          >
            <Minus className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
          <span className="w-8 text-center font-display text-body tabular text-ink">{quantity}</span>
          <button
            type="button"
            aria-label="Increase quantity"
            onClick={() => setQuantity((value) => value + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-pill bg-sunken text-ink-muted"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-[1fr_auto] gap-3">
        <Button
          size="lg"
          width="full"
          disabled={!inStock}
          loading={addMutation.isPending}
          onClick={() => addMutation.mutate()}
        >
          {inStock ? 'Add to cart' : 'Out of stock'}
        </Button>
        <Button variant="secondary" size="lg" onClick={() => navigate(cartRoute)} aria-label="Open cart">
          <ShoppingCart className="h-5 w-5" strokeWidth={1.5} />
        </Button>
      </div>
    </Page>
  );
}
